import React, { useState } from 'react';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Search from '../../assets/network-banner-icons/search-checkmark.svg';
import Share from '../../assets/network-banner-icons/share.svg';
import Analyze from '../../assets/network-banner-icons/analytics.svg';
import Getro from '../../assets/network-banner-icons/getro-g-network.svg';
import Bg from '../../assets/connect-background.jpg';

export const NetworkBanner = ({ networkSlug, setShowBanner }) => {
  const banners = [
    {
      id: 1,
      icon: Analyze,
      title: 'Get more out of your network',
      description: 'Get warm intros by centralizing your team’s contacts.',
      buttonText: '',
      next: `Let's see it`,
    },
    {
      id: 2,
      icon: Share,
      title: 'Centralize your extended network',
      description: 'Invite your company allies (founders, investors, advisors...) to share their contacts with you.',
      buttonText: 'Invite your team',
      next: 'Next',
      link: `/networks/${networkSlug}/settings/team-members`,
    },
    {
      id: 3,
      icon: Search,
      title: 'Search your network',
      description:
        'Find the exact match of contacts to help you with for sales, hiring, fundraising, partnerships, and more.',
      buttonText: 'Search your contacts',
      next: 'Next',
      link: `/networks/${networkSlug}/contacts`,
    },
    {
      id: 4,
      icon: Getro,
      title: 'Get warm intros faster',
      description: 'Use lists and automations that help close deals, meet talent, and get advice.',
      buttonText: 'Create a list',
      next: 'Got it',
      link: `/networks/${networkSlug}/lists/create`,
    },
  ];
  const [currentBanner, setCurrentBanner] = useState(banners[0]);

  return (
    <Flex
      sx={{
        flexDirection: ['column', 'row'],
        gap: '16px',
        maxWidth: '800px',
        width: '100%',
        p: '16px',
        position: 'relative',
        borderRadius: '4px',
        border: '1px solid #F4F0FF',
        background: '#FFF',
        overflow: 'hidden',
      }}
    >
      <Image src={Bg} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }} />
      <Image
        src={currentBanner.icon}
        sx={{
          position: 'relative',
          zIndex: 10,
          maxWidth: ['64px', '120px'],
          maxHeight: ['64px', '120px'],
          width: '100%',
          height: '100%',
        }}
      />

      <Flex
        key={currentBanner.id}
        sx={{
          width: '100%',
          gap: '16px',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 10,
          flex: 1,
          justifyContent: 'space-between',
        }}
      >
        <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            <Text color="text.dark" fontSize="20px" fontWeight="700" lineHeight="120%">
              {currentBanner.title}
            </Text>
            <Text color="text.main" fontSize="14px" fontWeight="400" lineHeight="120%">
              {currentBanner.description}
            </Text>
          </Flex>
        </Flex>
        <Flex
          sx={{
            flexDirection: ['column', 'row'],
            justifyContent: 'space-between',
            gap: currentBanner.buttonText ? '16px' : '0',
            width: '100%',
          }}
        >
          <Box>
            {currentBanner.buttonText && (
              <Button as={Link} to={currentBanner.link} size="small" variant="primary">
                {currentBanner.buttonText}
              </Button>
            )}
          </Box>
          <Flex sx={{ gap: '16px', alignItems: 'center', alignSelf: 'flex-end', zIndex: 10 }}>
            <Flex justifyContent="center" alignItems="center">
              {banners.map((banner) => (
                <Box
                  key={banner.id}
                  onClick={() => setCurrentBanner(banner)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Go to banner ${banner.id}: ${banner.title}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentBanner(banner);
                    }
                  }}
                  sx={{
                    height: '8px',
                    borderRadius: '9999px',
                    transition: 'all 0.3s ease-in-out',
                    minWidth: '24px',
                    width: '100%',
                    cursor: currentBanner.id === banner.id ? 'default' : 'pointer',
                    backgroundColor: currentBanner.id === banner.id ? 'purple.500' : 'neutral.100',
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'purple.500',
                      outlineOffset: '2px',
                    },
                    '&:not(:last-child)': {
                      marginRight: '8px',
                    },
                  }}
                />
              ))}
            </Flex>
            <Button
              icon={ChevronRight}
              iconPosition="right"
              size="small"
              justifyItems="center"
              variant={currentBanner.id === 1 ? 'primary' : 'secondary'}
              onClick={() => {
                const currentIndex = banners.findIndex((banner) => banner.id === currentBanner.id);
                const nextIndex = currentIndex === banners.length - 1 ? 0 : currentIndex + 1;
                setCurrentBanner(banners[nextIndex]);
                if (banners[currentIndex].id === 4) {
                  setShowBanner(false);
                }
              }}
            >
              {currentBanner.next}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

NetworkBanner.propTypes = {
  networkSlug: PropTypes.string.isRequired,
  setShowBanner: PropTypes.func.isRequired,
};
